import React from 'react';
import PropTypes from 'prop-types';
import { Button, Modal } from '@gsa/afp-component-library';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import solicitationNotifications, {
  solicitationModalMode,
  selectedSolicitationAtom,
} from '../utils/solicitation-notification-helpers';
import { DELETE_SOLICITATION } from '../queries';
import MarkUpText from '../../../components/MarkUpText/MarkUpText';
import '../styles/solicitation-modal.scss';
import { pageErrorNotifications } from '../../../utilities/error-handling';

const DeleteSolicitationModal = ({ onComplete, onClose }) => {
  const [modalMode, setModalMode] = useRecoilState(solicitationModalMode);
  const solicitation = useRecoilValue(selectedSolicitationAtom);
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );

  const [deleteSolicitation] = useMutation(DELETE_SOLICITATION, {
    onError: (requestError) => {
      setSolicitationNotification([
        ...pageErrorNotifications(requestError, {
          id: 'DELETE_SOLICITATION_ERROR',
          message: requestError.message,
          type: 'error',
          closeable: false,
          showInModal: false,
        }),
      ]);
    },
    onCompleted: (response) => {
      const { id, solicitationNumber } = response.deleteSolicitationById || {};
      if (id) {
        if (onComplete) onComplete();
        const message = `Solicitation ${solicitationNumber} has been successfully deleted.`;
        setSolicitationNotification([
          {
            id: 'DELETE_SOLICITATION',
            message: (
              <MarkUpText
                content={message}
                textToMarkUp={`Solicitation ${solicitationNumber}`}
                marker="strong"
              />
            ),
            type: 'success',
            closeable: true,
            showInModal: false,
            showOnHomePage: true,
          },
        ]);
      }
    },
  });

  const deleteSolMsg = `Are you sure you want to delete solicitation ${solicitation?.solicitationNumber}?`;

  return (
    <Modal
      title={
        <h3 className="margin-top-5" aria-label="Delete confirmation">
          Delete confirmation
        </h3>
      }
      variant="extra-large"
      className="solicitation-modal"
      onClose={onClose}
      actions={
        <>
          <Button
            variant="unstyled"
            className="margin-right-2"
            aria-label="Cancel"
            onClick={onClose}
            data-testid="solicitation-delete-cancel-btn"
            label="Cancel"
          />
          <Button
            variant="secondary"
            data-testid="solicitation-delete-remove-btn"
            aria-label="Delete"
            onClick={() => {
              void deleteSolicitation({
                variables: {
                  solicitationId: Number.parseInt(solicitation?.id, 10),
                  forceDelete: 'Y',
                },
              });
              setModalMode({ mode: null, showInModal: false });
              onClose();
            }}
            label="Delete"
          />
        </>
      }
      show={modalMode?.showInModal}
      onClick={onClose}
    >
      <div aria-label={deleteSolMsg}>
        Are you sure you want to delete solicitation{' '}
        <strong>{solicitation?.solicitationNumber}? </strong>
      </div>
    </Modal>
  );
};

DeleteSolicitationModal.propTypes = {
  onComplete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DeleteSolicitationModal;
